import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, ArrowRight, Mic, MicOff, Send, Loader2, Sparkles, Phone, Wrench, Package, Briefcase, Rocket } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Talk topics — asked right after email so we can route the visitor correctly.
// Career visitors are steered to text chat (voice credits are reserved for
// business / product / project enquiries).
const TOPICS = [
  { id: "services", label: "Services",             icon: Wrench,    desc: "AI, software, cloud, marketing, staff aug." },
  { id: "products", label: "Products",             icon: Package,   desc: "Acadmax, HelloSir.ai, UniCRM.ai and more." },
  { id: "career",   label: "Career",               icon: Briefcase, desc: "Roles, internships, and how to join us." },
  { id: "project",  label: "Discuss your project", icon: Rocket,    desc: "Scope, quote and a realistic delivery plan." },
];

// Minimal, popular country dial-codes (search-friendly)
const COUNTRIES = [
  { code: "GB", dial: "+44", name: "United Kingdom" },
  { code: "US", dial: "+1", name: "United States" },
  { code: "PK", dial: "+92", name: "Pakistan" },
  { code: "AE", dial: "+971", name: "United Arab Emirates" },
  { code: "SA", dial: "+966", name: "Saudi Arabia" },
  { code: "DE", dial: "+49", name: "Germany" },
  { code: "FR", dial: "+33", name: "France" },
  { code: "IN", dial: "+91", name: "India" },
  { code: "CA", dial: "+1", name: "Canada" },
  { code: "AU", dial: "+61", name: "Australia" },
  { code: "SG", dial: "+65", name: "Singapore" },
  { code: "QA", dial: "+974", name: "Qatar" },
  { code: "KW", dial: "+965", name: "Kuwait" },
  { code: "BH", dial: "+973", name: "Bahrain" },
  { code: "OM", dial: "+968", name: "Oman" },
  { code: "TR", dial: "+90", name: "Turkey" },
  { code: "EG", dial: "+20", name: "Egypt" },
  { code: "NL", dial: "+31", name: "Netherlands" },
  { code: "IT", dial: "+39", name: "Italy" },
  { code: "ES", dial: "+34", name: "Spain" },
  { code: "CH", dial: "+41", name: "Switzerland" },
];

const VOICE_LIMIT_MS = 180_000; // 180 seconds

export default function AIAgentModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // 0:name 1:email 2:phone 3:question 4:voice 5:chat
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dial, setDial] = useState("+44");
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountries, setShowCountries] = useState(false);
  const [phoneLocal, setPhoneLocal] = useState("");
  const [topic, setTopic] = useState("");        // NEW: services | products | career | project
  const [question, setQuestion] = useState("");

  useEffect(() => {
    const onOpen = () => { setOpen(true); setStep(0); setTopic(""); };
    window.addEventListener("open-ai-agent", onOpen);
    return () => window.removeEventListener("open-ai-agent", onOpen);
  }, []);

  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q));
  }, [countrySearch]);

  const close = () => {
    // If a call is live, stop it first so audio doesn't linger during exit animation
    try { voiceTeardownRef.current?.(); } catch { /* noop */ }
    setOpen(false);
  };
  const next = () => setStep((s) => s + 1);
  const phone = `${dial} ${phoneLocal}`.trim();
  const voiceTeardownRef = useRef(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-slate-950/70 backdrop-blur-md flex items-end md:items-center justify-center p-3 md:p-6"
          data-testid="ai-agent-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="ai-agent-modal relative w-full max-w-xl rounded-[28px] overflow-hidden border border-primary/30 bg-slate-950 text-white shadow-[0_35px_100px_-30px_hsl(var(--primary)/0.5)]"
          >
            {/* Futuristic grid backdrop */}
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.15] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(hsl(var(--primary)/0.6) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)/0.6) 1px, transparent 1px)",
                backgroundSize: "36px 36px",
                maskImage: "radial-gradient(ellipse at center, black 55%, transparent 100%)",
              }}
            />
            <div aria-hidden="true" className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/25 blur-3xl pointer-events-none" />

            <div className="relative p-6 md:p-8">
              {/* Header */}
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-2xl bg-primary/20 text-primary flex items-center justify-center">
                      <Bot className="w-6 h-6" strokeWidth={1.7} />
                    </div>
                    <span aria-hidden="true" className="absolute inset-0 rounded-2xl border border-primary/50 animate-ping" style={{ animationDuration: "3s" }} />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-primary">CloudLab · AI Agent</div>
                    <div className="font-heading font-bold text-lg leading-tight">Let&apos;s talk.</div>
                  </div>
                </div>
                <button data-testid="ai-agent-close" onClick={close} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Progress dots — 5 preparation stages (name/email/topic/phone/question) */}
              <div className="flex items-center gap-1.5 mb-6">
                {[0, 1, 2, 3, 4].map((i) => {
                  const cur = Math.min(step, 4);
                  return <span key={i} className={`h-1 rounded-full transition-all ${i === cur ? "w-8 bg-primary" : i < cur ? "w-4 bg-primary/60" : "w-4 bg-white/15"}`} />;
                })}
              </div>

              <AnimatePresence mode="wait">
                {step === 0 && (
                  <StepShell key="name" prompt="What's your name?" hint="I'll use this to personalise our conversation.">
                    <input autoFocus data-testid="ai-agent-input-name" value={name} onChange={(e) => setName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && name.trim() && next()}
                      className="w-full bg-slate-900/70 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-primary" placeholder="e.g. Sarah" />
                    <NextBtn disabled={!name.trim()} onClick={next} label="Continue" testid="ai-agent-next-name" />
                  </StepShell>
                )}
                {step === 1 && (
                  <StepShell key="email" prompt={`Nice to meet you, ${name.split(" ")[0]}. What's your email?`} hint="So we can follow up if we get cut off.">
                    <input autoFocus type="email" data-testid="ai-agent-input-email" value={email} onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && emailValid && next()}
                      className="w-full bg-slate-900/70 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-primary" placeholder="you@company.com" />
                    <NextBtn disabled={!emailValid} onClick={next} label="Continue" testid="ai-agent-next-email" />
                  </StepShell>
                )}
                {step === 2 && (
                  <StepShell key="topic" prompt="What would you like to talk about?" hint="Pick one — I'll route you to the right expert.">
                    <div className="grid sm:grid-cols-2 gap-2.5" data-testid="ai-agent-topic-grid">
                      {TOPICS.map((t) => {
                        const Icon = t.icon;
                        const active = topic === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            data-testid={`ai-agent-topic-${t.id}`}
                            aria-pressed={active}
                            onClick={() => {
                              setTopic(t.id);
                              // Career visitors go straight to the question — skip phone → text-chat only
                              setStep(t.id === "career" ? 4 : 3);
                            }}
                            className={`text-left flex items-start gap-3 rounded-2xl border-2 p-3.5 transition-all ${active ? "border-primary bg-primary/[0.08]" : "border-white/15 hover:border-primary/60"}`}
                          >
                            <div className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center ${active ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary"}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{t.label}</div>
                              <div className="text-xs text-white/60 leading-relaxed mt-0.5">{t.desc}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </StepShell>
                )}
                {step === 3 && (
                  <StepShell key="phone" prompt="Phone or WhatsApp number?" hint="Pick your country, then type the local number.">
                    <div className="relative">
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setShowCountries((v) => !v)} data-testid="ai-agent-country-btn"
                          className="shrink-0 min-w-[110px] bg-slate-900/70 border border-white/20 rounded-xl px-3 py-3 text-sm text-left flex items-center justify-between gap-2 hover:border-primary/50">
                          <span className="font-mono">{dial}</span>
                          <span className="text-white/50 text-xs">▾</span>
                        </button>
                        <input autoFocus data-testid="ai-agent-input-phone" value={phoneLocal} onChange={(e) => setPhoneLocal(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && phoneLocal.trim() && next()}
                          className="flex-1 bg-slate-900/70 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-primary" placeholder="7380 885 506" />
                      </div>
                      {showCountries && (
                        <div className="absolute z-10 mt-2 w-full max-w-md rounded-xl border border-white/20 bg-slate-900 shadow-2xl overflow-hidden">
                          <input autoFocus placeholder="Search country or +code…" value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)}
                            data-testid="ai-agent-country-search"
                            className="w-full bg-transparent border-b border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none" />
                          <div className="max-h-56 overflow-y-auto">
                            {filteredCountries.map((c) => (
                              <button key={c.code} type="button" onClick={() => { setDial(c.dial); setShowCountries(false); setCountrySearch(""); }}
                                data-testid={`ai-agent-country-${c.code}`}
                                className="w-full text-left px-4 py-2.5 hover:bg-white/10 flex items-center justify-between text-sm">
                                <span>{c.name}</span>
                                <span className="text-white/50 font-mono text-xs">{c.dial}</span>
                              </button>
                            ))}
                            {filteredCountries.length === 0 && <div className="px-4 py-6 text-center text-white/40 text-sm">No match.</div>}
                          </div>
                        </div>
                      )}
                    </div>
                    <NextBtn disabled={!phoneLocal.trim()} onClick={() => setStep(4)} label="Continue" testid="ai-agent-next-phone" />
                  </StepShell>
                )}
                {step === 4 && (
                  <StepShell key="q" prompt={topic === "career" ? "Tell me a bit about the role you're interested in." : "What would you like to discuss?"} hint={topic === "career" ? "Team, seniority, timeline, portfolio link — anything helpful." : "Team size, project idea, product, timeline — anything."}>
                    <textarea autoFocus rows={3} data-testid="ai-agent-input-question" value={question} onChange={(e) => setQuestion(e.target.value)}
                      className="w-full bg-slate-900/70 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-primary" placeholder={topic === "career" ? "I'm a senior engineer looking to join the AI team…" : "I need an AI agent for our university admissions team…"} />
                    <div className="flex flex-wrap gap-2">
                      {topic !== "career" && (
                        <NextBtn disabled={!question.trim()} onClick={() => setStep(5)} label="Start voice call" icon={<Phone className="w-4 h-4" />} testid="ai-agent-start-voice" />
                      )}
                      <button onClick={() => setStep(6)} data-testid="ai-agent-start-chat"
                        disabled={!question.trim()}
                        className="rounded-full px-5 py-2.5 border border-white/25 text-sm hover:bg-white/10 transition-colors disabled:opacity-50">
                        {topic === "career" ? "Start text chat →" : "Prefer text chat →"}
                      </button>
                    </div>
                    {topic === "career" && (
                      <p className="text-xs text-white/50 leading-relaxed">Career enquiries are handled over text chat. For business, product or project talks pick voice on the previous step.</p>
                    )}
                  </StepShell>
                )}
                {step === 5 && <VoiceStep key="voice" name={name} email={email} phone={phone} question={question} topic={topic} onFallback={() => setStep(6)} teardownRef={voiceTeardownRef} />}
                {step === 6 && <TextChatStep key="chat" name={name} email={email} phone={phone} question={question} topic={topic} />}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StepShell({ prompt, hint, children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.28 }} className="space-y-4">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary mb-1 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> AI Agent</div>
        <div className="font-heading font-bold text-2xl leading-tight">{prompt}</div>
        {hint && <p className="mt-1 text-sm text-white/60">{hint}</p>}
      </div>
      {children}
    </motion.div>
  );
}

function NextBtn({ disabled, onClick, label, icon, testid }) {
  return (
    <button data-testid={testid} disabled={disabled} onClick={onClick}
      className="group rounded-full pl-5 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground disabled:opacity-50">
      <span>{label}</span>
      <span className="w-8 h-8 rounded-full bg-slate-950 text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
        {icon || <ArrowRight className="w-4 h-4" />}
      </span>
    </button>
  );
}

// ---- Voice step: OpenAI Realtime WebRTC, hard-capped at 180s ----
function VoiceStep({ name, email, phone, question, onFallback, teardownRef }) {
  const [connecting, setConnecting] = useState(true);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const initedRef = useRef(false);
  const transcriptRef = useRef([]); // [{role, content, ts}]
  const startedAtRef = useRef(null);
  const savedRef = useRef(false);

  // Save transcript once (idempotent) — called on teardown / fallback
  const saveConversation = async (reason) => {
    if (savedRef.current) return;
    savedRef.current = true;
    const items = transcriptRef.current;
    if (!items.length) return; // nothing to save
    try {
      await api.post("/ai-agent/conversations", {
        name, email, phone, question,
        channel: "voice",
        started_at: startedAtRef.current,
        ended_reason: reason || "user_ended",
        transcript: items,
      });
    } catch { /* silent — best effort */ }
  };

  function teardown(reason) {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { dcRef.current?.close(); } catch { /* noop */ }
    try { pcRef.current?.getSenders?.().forEach((s) => { try { s.track?.stop(); } catch { /* noop */ } }); } catch { /* noop */ }
    try { pcRef.current?.close(); } catch { /* noop */ }
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.srcObject = null;
        audioRef.current.remove();
      }
    } catch { /* noop */ }
    clearInterval(timerRef.current);
    // Fire-and-forget transcript save
    saveConversation(reason);
  }

  // Expose teardown to the parent so closing the modal instantly kills the call
  useEffect(() => {
    if (teardownRef) teardownRef.current = () => teardown("modal_closed");
    return () => { if (teardownRef) teardownRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initedRef.current) return; // guard against StrictMode double-invoke
    initedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        startedAtRef.current = new Date().toISOString();
        const { data } = await api.post("/ai-agent/voice-session", { name, email, phone, question });
        const ephemeralKey = data?.value || data?.client_secret?.value;
        if (!ephemeralKey) throw new Error("No ephemeral token");

        const pc = new RTCPeerConnection();
        pcRef.current = pc;

        // Remote audio (agent voice) — DO NOT append to DOM; setting srcObject on a detached <audio> plays via browser
        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        audioEl.playsInline = true;
        audioRef.current = audioEl;
        pc.ontrack = (e) => { audioEl.srcObject = e.streams[0]; };

        // Mic with echo cancellation to prevent the agent's voice looping back
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        streamRef.current = stream;
        stream.getTracks().forEach((t) => pc.addTrack(t, stream));

        // Data channel — capture transcripts as they stream in
        const dc = pc.createDataChannel("oai-events");
        dcRef.current = dc;
        dc.addEventListener("message", (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "response.audio_transcript.done" && msg.transcript) {
              transcriptRef.current.push({ role: "assistant", content: msg.transcript, ts: new Date().toISOString() });
            } else if (msg.type === "conversation.item.input_audio_transcription.completed" && msg.transcript) {
              transcriptRef.current.push({ role: "user", content: msg.transcript, ts: new Date().toISOString() });
            }
          } catch { /* ignore malformed events */ }
        });

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const r = await fetch(`https://api.openai.com/v1/realtime/calls?model=${data.model || "gpt-realtime"}`, {
          method: "POST", body: offer.sdp,
          headers: { Authorization: `Bearer ${ephemeralKey}`, "Content-Type": "application/sdp" },
        });
        const answerSdp = await r.text();
        if (!r.ok) throw new Error(answerSdp);
        await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
        if (!cancelled) { setConnected(true); setConnecting(false); }
      } catch (e) {
        if (!cancelled) { setError(e.message || "Could not start voice"); setConnecting(false); }
      }
    })();
    return () => { cancelled = true; teardown("unmount"); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!connected) return;
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        const nextEl = s + 1000;
        if (nextEl >= VOICE_LIMIT_MS) { teardown("time_limit"); toast.info("Voice time is up — switching to text chat."); onFallback(); }
        return nextEl;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  function toggleMute() {
    const track = streamRef.current?.getAudioTracks?.()[0];
    if (track) { track.enabled = !track.enabled; setMuted(!track.enabled); }
  }
  const remaining = Math.max(0, VOICE_LIMIT_MS - elapsed);
  const mm = String(Math.floor(remaining / 60000)).padStart(1, "0");
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, "0");

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 text-center">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary mb-1">Voice · Live</div>
        <div className="font-heading font-bold text-2xl">Speak with the AI Agent</div>
      </div>
      <div className="relative mx-auto w-40 h-40 rounded-full bg-primary/15 flex items-center justify-center">
        <span aria-hidden="true" className="absolute inset-0 rounded-full border border-primary/50 animate-ping" style={{ animationDuration: "2.4s" }} />
        <span aria-hidden="true" className="absolute inset-4 rounded-full border border-primary/40 animate-ping" style={{ animationDuration: "3.2s" }} />
        <div className="relative w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
          {connecting ? <Loader2 className="w-8 h-8 animate-spin" /> : <Bot className="w-10 h-10" strokeWidth={1.6} />}
        </div>
      </div>
      <div className="text-4xl font-heading font-black tabular-nums text-primary" data-testid="ai-agent-voice-timer">
        {mm}:{ss}
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex items-center justify-center gap-3">
        <button onClick={toggleMute} disabled={!connected} data-testid="ai-agent-voice-mute"
          className="rounded-full px-5 py-2.5 border border-white/25 text-sm hover:bg-white/10 inline-flex items-center gap-2">
          {muted ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
          {muted ? "Unmute" : "Mute"}
        </button>
        <button onClick={() => { teardown("user_ended"); onFallback(); }} data-testid="ai-agent-voice-end"
          className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium">
          End call · Continue in chat
        </button>
      </div>
      <p className="text-xs text-white/50">Sessions are capped at 3 minutes. After that we&apos;ll continue in text chat.</p>
    </motion.div>
  );
}

// ---- Text-chat step: gpt-4o-mini via /api/ai-agent/text-chat ----
function TextChatStep({ name, email, phone, question }) {
  const [messages, setMessages] = useState(() => (
    question ? [{ role: "user", content: question }] : []
  ));
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const scrollerRef = useRef(null);
  const messagesRef = useRef(messages);
  const savedRef = useRef(false);
  const startedAtRef = useRef(new Date().toISOString());

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    // Kick off first reply if we have an initial question and no assistant reply yet
    if (messages.length === 1 && messages[0].role === "user") send([...messages]);
    // Persist transcript on unmount (best-effort)
    return () => {
      if (savedRef.current) return;
      const items = messagesRef.current;
      if (!items || items.length < 2) return; // need at least one exchange
      savedRef.current = true;
      api.post("/ai-agent/conversations", {
        name, email, phone, question,
        channel: "text",
        started_at: startedAtRef.current,
        ended_reason: "unmount",
        transcript: items.map((m) => ({ role: m.role, content: m.content })),
      }).catch(() => { /* silent */ });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { scrollerRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages, reply, busy]);

  async function send(msgs) {
    setBusy(true); setReply("");
    try {
      const { data } = await api.post("/ai-agent/text-chat", { name, email, phone, messages: msgs });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      toast.error("Chat failed. Please try again.");
    } finally { setBusy(false); }
  }
  function submit(e) {
    e.preventDefault();
    const t = draft.trim(); if (!t) return;
    const next = [...messages, { role: "user", content: t }];
    setMessages(next); setDraft(""); send(next);
  }

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary mb-1">Text Chat</div>
        <div className="font-heading font-bold text-2xl">Ask the AI Agent anything.</div>
      </div>
      <div ref={scrollerRef} data-testid="ai-agent-chat-log" className="max-h-72 min-h-40 overflow-y-auto space-y-3 pr-1 rounded-xl border border-white/15 bg-white/5 p-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-white/12 text-white"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className="max-w-[70%] px-3 py-2 rounded-2xl text-sm bg-white/10 text-white/70 inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div></div>
        )}
      </div>
      <form onSubmit={submit} className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="ai-agent-chat-input"
          className="flex-1 bg-slate-900/70 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-primary" placeholder="Type your message…" />
        <button type="submit" disabled={busy || !draft.trim()} data-testid="ai-agent-chat-send"
          className="rounded-xl px-4 py-3 bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </motion.div>
  );
}
